import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import {
  PagedResponseMeta,
  ParentService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { FamilyCard, FamilyWithExtras } from './components/FamilyCard';

type FilterType = 'associated' | 'favorite' | 'contacted' | 'hidden';

const FILTERS: Array<{ key: FilterType; label: string }> = [
  { key: 'associated', label: 'All' },
  { key: 'favorite', label: 'Favorites' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'hidden', label: 'Hidden' },
];

export const YourFamilies = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const sitterId = pathId ? Number(pathId) : undefined;
  const pageParam = Number(searchParams.get('page') || 1) || 1;

  const initialFilter: FilterType = ((): FilterType => {
    if (searchParams.get('favorite')) return 'favorite';
    if (searchParams.get('contacted')) return 'contacted';
    if (searchParams.get('hidden')) return 'hidden';
    if (searchParams.get('associated')) return 'associated';
    return 'favorite';
  })();

  const [selectedType, setSelectedType] = useState<FilterType>(initialFilter);
  const [families, setFamilies] = useState<FamilyWithExtras[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState<UserNameFilterOption | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Families';
  }, []);

  useEffect(() => {
    if (!user?.id || !sitterId) return;
    if (user.id !== sitterId) {
      navigate(`/sitter/${user.id}/your-families`, { replace: true });
    }
  }, [user?.id, sitterId, navigate]);

  const parentIdFilter = searchParams.get('parentId');

  const fetchFamilies = useCallback(
    async (
      id: number,
      filter: FilterType,
      page: number,
      parentIdQ?: string | null,
      familyName?: string,
    ): Promise<void> => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          active: true,
          userId: id,
          sitterId: id,
          flagged: false,
          page,
        };
        params[filter] = true;
        if (filter !== 'hidden' && filter !== 'associated') {
          params.hidden = false;
        }
        if (parentIdQ) params.parentId = parentIdQ;
        if (familyName) params.name = familyName;
        const resp = (await ParentService.getByFilter({ params })).data;
        setFamilies((resp.data ?? []) as FamilyWithExtras[]);
        setMeta(resp.meta);
      } catch (err) {
        console.warn('[YourFamilies] load failed', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id) return;
    fetchFamilies(
      user.id,
      selectedType,
      pageParam,
      parentIdFilter,
      selectedFamily?.name,
    );
  }, [user?.id, selectedType, pageParam, parentIdFilter, selectedFamily, fetchFamilies]);

  const onNameFilter = (u: UserNameFilterOption | null): void => {
    setSelectedFamily(u);
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set('parentId', String(u.id));
        else n.delete('parentId');
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const setFilter = (next: FilterType): void => {
    setSearchParams((sp) => {
      const nextSp = new URLSearchParams(sp);
      FILTERS.forEach((f) => nextSp.delete(f.key));
      nextSp.set(next, 'true');
      nextSp.set('page', '1');
      return nextSp;
    });
    setSelectedType(next);
  };

  const goToPage = (page: number): void => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('page', String(page));
      return next;
    });
  };

  const updateFamily = (
    id: number,
    updater: (f: FamilyWithExtras) => FamilyWithExtras,
  ): void => {
    setFamilies((prev) => prev.map((f) => (f.id === id ? updater(f) : f)));
  };

  const renderEmpty = (): JSX.Element => {
    const familyName = selectedFamily?.name;
    if (selectedType === 'associated' || selectedType === 'contacted') {
      return (
        <div>
          <h4 className='font-semibold mb-1'>
            You have not been in contact with any families
            {familyName ? ` named ${familyName}` : ''}.
          </h4>
          <p>Families that have booked you or sent you a message will appear here.</p>
        </div>
      );
    }
    if (selectedType === 'favorite') {
      return (
        <div>
          <h4 className='font-semibold mb-1'>
            You haven&apos;t selected any favorites
            {familyName ? ` named ${familyName}` : ''}.
          </h4>
          <p>
            Find your favorite families easily by clicking the heart over their photo.<br />
            You can{' '}
            <Button
              id='null-to-contacted'
              buttonType={ButtonType.tertiary}
              onClick={(): void => setFilter('contacted')}
              className='underline text-sapphire'
            >
              select favorites
            </Button>{' '}
            from families that have booked or contacted you.
          </p>
        </div>
      );
    }
    if (selectedType === 'hidden') {
      if (selectedFamily) {
        return (
          <h4 className='font-semibold'>
            We could not find &quot;{selectedFamily.name}&quot;.
          </h4>
        );
      }
      return (
        <div>
          <h4 className='font-semibold mb-1'>
            Your availability is shown to all families right now.
          </h4>
          <p>
            If you&apos;d prefer not to get booking requests from a particular family
            you can hide your availability from them. Just select &quot;Hide availability&quot;
            from the options menu next to that family.
          </p>
        </div>
      );
    }
    return <div />;
  };

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold'>Your Families</h1>
      <p className='mb-5 text-neutral-700'>
        Families that you&apos;ve worked for, been in contact with, or favorited.
      </p>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-6'>
        <div className='sm:col-span-3'>
          <label htmlFor='typeSelect' className='block text-sm mb-1'>Type:</label>
          <select
            id='typeSelect'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={selectedType}
            onChange={(e): void => setFilter(e.target.value as FilterType)}
            disabled={loading}
          >
            {FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className='sm:col-span-3'>
          <UserNameFilter
            kind='parent'
            viewerId={user?.id}
            label={t('common.familyLabel')}
            onSelect={onNameFilter}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-9 gap-4'>
        <div className='lg:col-span-9'>
          {loading ? (
            <p className='text-neutral-600 text-center py-4'>Loading…</p>
          ) : families.length === 0 ? (
            <div className='us-card bg-white rounded p-6'>{renderEmpty()}</div>
          ) : (
            <>
              {families.map((family) => (
                <FamilyCard
                  key={family.id}
                  family={family}
                  selectedType={selectedType}
                  sitterId={user?.id ?? 0}
                  onUpdate={(updater): void => {
                    if (family.id != null) updateFamily(family.id, updater);
                  }}
                />
              ))}

              {meta && meta.pageCount !== undefined && meta.pageCount > 1 && (
                <div className='flex items-center justify-center gap-3 mt-4'>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={(meta.page ?? 1) <= 1}
                    onClick={(): void => goToPage((meta.page ?? 1) - 1)}
                  >
                    Previous
                  </Button>
                  <span className='text-sm text-neutral-700'>
                    Page {meta.page} of {meta.pageCount}
                  </span>
                  <Button
                    buttonType={ButtonType.secondary}
                    disabled={meta.lastPage === true || (meta.page ?? 1) >= (meta.pageCount ?? 1)}
                    onClick={(): void => goToPage((meta.page ?? 1) + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});
